import { useState } from "react";
import { ArrowRight, Loader2, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { submitBlogLead } from "@/lib/api";

/**
 * Inline lead-capture card embedded inside blog posts. Sends reader
 * straight into the HPM lead marketplace where vetted civil-rights firms
 * compete to take their case — free to the reader.
 */
export default function BlogLeadForm({
    slug,
    topicKey,
    category,
    accent = "#FF3B30",
    variant = "inline", // inline | drawer
    slot = "main", // mid | end | drawer
    onSubmitted,
}) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        zip_code: "",
        state: "",
        situation: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null);

    const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error("Add your first name so we can reach you.");
            return;
        }
        if (!form.email.trim() && !form.phone.trim()) {
            toast.error("Add an email OR a phone — we need one way to reach you.");
            return;
        }
        if (form.situation.trim().length < 10) {
            toast.error("One sentence about what happened — even 10 words is enough.");
            return;
        }
        setSubmitting(true);
        try {
            const lead = await submitBlogLead({
                slug,
                topic_key: topicKey || "",
                language: "en",
                consent_share: true,
                ...form,
            });
            setDone(lead);
            onSubmitted?.(lead);
            toast.success("You're in. A vetted attorney will reach out.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't submit. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div
                className="border-2 p-6 bg-white/5"
                style={{ borderColor: accent }}
                data-testid="blog-lead-success"
            >
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: accent }}>
                    REQUEST RECEIVED
                </div>
                <div className="font-anton text-2xl uppercase mb-3">
                    You're in. The system just got smaller.
                </div>
                <p className="font-sans text-sm text-white/80 leading-relaxed">
                    A vetted attorney in {form.state || "your area"} will reach you within{" "}
                    <span className="font-bold text-white">24 hours</span>. While you wait — run
                    your situation through the 10 bots for a free, full analysis.
                </p>
                <a
                    href="/hpm-submit"
                    className="btn-brutal mt-5 inline-flex"
                    data-testid="blog-lead-success-cta"
                >
                    START FREE 10-BOT ANALYSIS <ArrowRight size={14} />
                </a>
            </div>
        );
    }

    const isDrawer = variant === "drawer";

    return (
        <form
            onSubmit={onSubmit}
            className={`border-2 ${isDrawer ? "border-white" : "border-white/30"} p-6 bg-[#0a0a0a]`}
            style={!isDrawer ? { borderColor: accent } : undefined}
            data-testid={`blog-lead-form-${variant}-${slot}`}
        >
            <div
                className="font-mono text-[10px] uppercase tracking-[0.3em] mb-2"
                style={{ color: accent }}
            >
                FREE LEGAL HELP — NO COST TO YOU
            </div>
            <h3 className="font-anton text-2xl sm:text-3xl uppercase leading-tight">
                Is this happening to you <span style={{ color: accent }}>right now?</span>
            </h3>
            <p className="font-sans text-sm text-white/70 mt-2 mb-5 leading-relaxed">
                Tell us what's going on. A vetted civil-rights attorney will reach you within 24
                hours. <span className="font-bold text-white">You pay nothing.</span>
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                    data-testid="blog-lead-name"
                    placeholder="First name"
                    value={form.name}
                    onChange={update("name")}
                    className="bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm text-white placeholder-white/40 focus:border-white outline-none"
                />
                <input
                    data-testid="blog-lead-email"
                    type="email"
                    placeholder="Email"
                    value={form.email}
                    onChange={update("email")}
                    className="bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm text-white placeholder-white/40 focus:border-white outline-none"
                />
                <input
                    data-testid="blog-lead-phone"
                    placeholder="Phone (optional)"
                    value={form.phone}
                    onChange={update("phone")}
                    className="bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm text-white placeholder-white/40 focus:border-white outline-none"
                />
                <div className="grid grid-cols-2 gap-3">
                    <input
                        data-testid="blog-lead-zip"
                        placeholder="ZIP"
                        value={form.zip_code}
                        onChange={update("zip_code")}
                        className="bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm text-white placeholder-white/40 focus:border-white outline-none"
                    />
                    <input
                        data-testid="blog-lead-state"
                        placeholder="State"
                        value={form.state}
                        onChange={update("state")}
                        className="bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm text-white placeholder-white/40 focus:border-white outline-none"
                    />
                </div>
            </div>
            <textarea
                data-testid="blog-lead-situation"
                placeholder="What happened? One or two sentences — when, where, and what you need."
                value={form.situation}
                onChange={update("situation")}
                rows={3}
                className="mt-3 w-full bg-transparent border-2 border-white/30 px-3 py-2 font-sans text-sm text-white placeholder-white/40 focus:border-white outline-none resize-none"
            />

            <button
                type="submit"
                disabled={submitting}
                data-testid="blog-lead-submit"
                className="btn-brutal btn-brutal-red mt-4 w-full justify-center disabled:opacity-50"
            >
                {submitting ? (
                    <>
                        <Loader2 size={14} className="animate-spin" /> SENDING...
                    </>
                ) : (
                    <>
                        GET FREE LEGAL HELP <ArrowRight size={14} />
                    </>
                )}
            </button>

            <div className="mt-3 flex items-start gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                <ShieldCheck size={11} className="mt-[1px] flex-shrink-0" />
                <span>
                    Your info is shared only with vetted civil-rights firms in your state. You can
                    opt out anytime. Free to you — firms pay HPM, never you.
                </span>
            </div>
        </form>
    );
}
